/**
 * hooks.ts — Hooks utilitários do site institucional Nectaris
 * Separados dos componentes para compatibilidade com react-refresh.
 */

import { useEffect, useRef, useState } from "react";

/* ============================================================
   HOOK useReveal — IntersectionObserver para reveal animation
   ============================================================ */
export function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal--in");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, []);

  return ref;
}

/* ============================================================
   HOOK useScrolled — detecta scroll > threshold
   ============================================================ */
export function useScrolled(threshold = 24) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > threshold);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [threshold]);

  return scrolled;
}

/* ============================================================
   HOOK useMobileMenu — hamburguer open/close
   ============================================================ */
export function useMobileMenu() {
  const [open, setOpen] = useState(false);

  function toggle() {
    setOpen((v) => !v);
  }

  function close() {
    setOpen(false);
  }

  // Fecha ao pressionar Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return { open, toggle, close };
}
